import { transcriptEvidenceAtom } from '@/atoms/panelAtoms';
import { selectedNoteTextAtom } from '@/atoms/selectedNoteTextAtoms';
import { scrollToTranscriptHighlight } from '@heidi/session';
import { TabType } from '@heidi/session/contracts';
import { useSessionWindows } from '@heidi/session/data';

import { useOpenEvidencePanel } from '@/hooks/useOpenEvidencePanel';
import { useSessionStage } from '@/hooks/useSessionStage';
import { useStageAskHeidiQuery } from '@/hooks/useStageAskHeidiQuery';
import { track } from '@/lib/analytics/track';
import {
  composeSelectedTextQuestionPrompt,
  composeSelectionActionPrompt,
  composeSelectionKeyPointsPrompt,
  SELECTION_INSIGHT_ACTION_IDS,
  type SelectionInsightActionId,
} from '@/lib/selection-ai-prompts';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { customFetchEventSource } from '@/utils/fetchEventSource';
import { toast } from '@heidi/design-system/toast';
import { isEditorLive } from '@heidi/editor/core';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { ContentType } from '@heidi/schema/ml-scribe';
import { type Editor } from '@tiptap/react';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { getRangeSourceText, selectionSourceMatches } from '../selection-source-text';
import {
  inlineSelectionEditActiveCountAtom,
  inlineSelectionEditRequestAtom,
  type InlineEditActionId,
  type InlineEditIntent,
  type InlineSelectionEditCustomPromptRequest,
  type InlineSelectionEditInsightRequest,
  type InlineSelectionEditPresetRequest,
  type InlineSelectionEditPromptInputRequest,
  type InlineSelectionEditRequest,
} from './atoms';
import {
  createInlineSelectionEditPlugin,
  dispatchInlineEditMeta,
  getInlineEditPluginState,
  inlineSelectionEditPluginKey,
} from './inline-edit-decorations';
import {
  normalizeInlineEditResult,
  toInsertableBlockHtml,
  toInsertableHtml,
} from './insertable-text';
import { isUnansweredInlineResponse } from './is-unanswered-response';
import { parseQuoteShapedTranscriptExcerpts } from './parse-transcript-excerpts';

const messages = defineMessages({
  editFailed: {
    defaultMessage: "Couldn't finish the edit — your note wasn't changed.",
    id: 'aJyrTklsv4',
    description:
      'Toast shown when an inline AI edit fails or returns nothing; the note is untouched',
  },
  selectionChanged: {
    defaultMessage:
      "Your selection changed while Heidi was working — your note wasn't changed.",
    id: 'RwpTKuMgou',
    description:
      'Toast shown when highlighted text changes during inline AI generation so a stale result is not applied',
  },
  noTranscriptExcerpts: {
    defaultMessage:
      "Couldn't locate timestamped transcript quotes in this response — open the Transcript tab to search manually.",
    id: 'A1vd0nWOqQ',
    description:
      'Toast when View in Transcript is clicked but the AI response has no parseable quote lines',
  },
});

/** List nodes are registered only when problem-based charting is on. */
const editorSupportsLists = (editor: Editor): boolean =>
  Boolean(editor.schema.nodes.bulletList && editor.schema.nodes.orderedList);

export type InlineEditPhase = 'idle' | 'prompting' | 'generating' | 'review';

interface InlineEditState {
  phase: InlineEditPhase;
  actionId: InlineEditActionId | null;
  resultText: string | null;
  /** Partial SSE text while phase is `generating`. */
  streamingText: string | null;
  intent: InlineEditIntent;
  /** The originally highlighted text (used for prompts and hand-offs). */
  sourceText: string | null;
  /** The exact prompt sent for this run (used by the Ask-in-chat hand-off). */
  promptUsed: string | null;
}

const IDLE_STATE: InlineEditState = {
  phase: 'idle',
  actionId: null,
  resultText: null,
  streamingText: null,
  intent: 'edit',
  sourceText: null,
  promptUsed: null,
};

type RunnableInlineSelectionEditRequest =
  | InlineSelectionEditPresetRequest
  | InlineSelectionEditInsightRequest
  | InlineSelectionEditCustomPromptRequest;

const isSelectionInsightActionId = (
  actionId: InlineEditActionId
): actionId is SelectionInsightActionId =>
  (SELECTION_INSIGHT_ACTION_IDS as readonly string[]).includes(actionId);

const isPromptInputRequest = (
  editRequest: InlineSelectionEditRequest
): editRequest is InlineSelectionEditPromptInputRequest =>
  'promptInput' in editRequest && editRequest.promptInput === true;

const resolveSelectionEditPrompt = (
  editRequest: RunnableInlineSelectionEditRequest
): string => {
  if ('customPrompt' in editRequest) {
    return editRequest.customPrompt;
  }
  if (editRequest.intent === 'insight') {
    return composeSelectionKeyPointsPrompt(editRequest.actionId, editRequest.text);
  }
  return composeSelectionActionPrompt(editRequest.actionId, editRequest.text);
};

/**
 * Controller for inline AI edits (selection → Ask Heidi): consumes edit
 * requests for its editor, drives the `ask-ai` SSE call, paints pending /
 * review decorations via the inline-edit plugin, and exposes accept / discard /
 * stop. The note document is only modified on accept.
 */
export const useInlineSelectionEdit = (editor: Editor, enabled: boolean) => {
  const intl = useIntl();
  const sessionId = useCurrentSessionId();
  const sessionStage = useSessionStage();
  const { ensureTabFocus } = useSessionWindows({ sessionId: sessionId ?? '' });
  const setTranscriptEvidence = useSetAtom(transcriptEvidenceAtom);
  const [request, setRequest] = useAtom(inlineSelectionEditRequestAtom);
  const setSelectedNoteText = useSetAtom(selectedNoteTextAtom);
  const [state, setState] = useState<InlineEditState>(IDLE_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const accumulatedRef = useRef('');
  const streamFlushRafRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  const instanceActiveRef = useRef(false);

  const cancelStreamingTextFlush = useCallback(() => {
    if (streamFlushRafRef.current === null) return;
    cancelAnimationFrame(streamFlushRafRef.current);
    streamFlushRafRef.current = null;
  }, []);

  const scheduleStreamingTextFlush = useCallback(() => {
    if (streamFlushRafRef.current !== null) return;
    streamFlushRafRef.current = requestAnimationFrame(() => {
      streamFlushRafRef.current = null;
      if (cancelledRef.current) return;
      const nextText = accumulatedRef.current;
      setState((prev) => {
        if (prev.phase !== 'generating') return prev;
        if (prev.streamingText === nextText) return prev;
        return { ...prev, streamingText: nextText || null };
      });
    });
  }, []);

  useEffect(() => {
    if (!enabled || !isEditorLive(editor, 'use-inline-selection-edit.registerPlugin'))
      return;
    editor.registerPlugin(createInlineSelectionEditPlugin());
    return () => {
      if (isEditorLive(editor, 'use-inline-selection-edit.unregisterPlugin')) {
        editor.unregisterPlugin(inlineSelectionEditPluginKey);
      }
    };
  }, [editor, enabled]);

  const setActiveCount = useSetAtom(inlineSelectionEditActiveCountAtom);
  useEffect(() => {
    const shouldBeActive = enabled && state.phase !== 'idle';

    if (shouldBeActive && !instanceActiveRef.current) {
      setActiveCount((count) => count + 1);
      instanceActiveRef.current = true;
    } else if (!shouldBeActive && instanceActiveRef.current) {
      setActiveCount((count) => Math.max(0, count - 1));
      instanceActiveRef.current = false;
    }

    return () => {
      if (instanceActiveRef.current) {
        setActiveCount((count) => Math.max(0, count - 1));
        instanceActiveRef.current = false;
      }
    };
  }, [enabled, state.phase, setActiveCount]);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    cancelStreamingTextFlush();
    abortRef.current?.abort();
    abortRef.current = null;
    if (isEditorLive(editor, 'use-inline-selection-edit.clearMeta')) {
      dispatchInlineEditMeta(editor, { type: 'clear' });
    }
    setState(IDLE_STATE);
  }, [editor, cancelStreamingTextFlush]);

  const abortForStaleSelection = useCallback(() => {
    toast({
      tone: 'destructive',
      title: intl.formatMessage(messages.selectionChanged),
    });
    reset();
  }, [intl, reset]);

  const start = useCallback(
    (editRequest: RunnableInlineSelectionEditRequest) => {
      if (!sessionId) return;

      const sourceText = getRangeSourceText(editor, {
        from: editRequest.from,
        to: editRequest.to,
      });
      if (!sourceText) {
        toast({
          tone: 'destructive',
          title: intl.formatMessage(messages.editFailed),
        });
        reset();
        return;
      }

      abortRef.current?.abort();
      setSelectedNoteText(null);

      dispatchInlineEditMeta(editor, {
        type: 'generating',
        from: editRequest.from,
        to: editRequest.to,
      });
      const prompt = resolveSelectionEditPrompt({ ...editRequest, text: sourceText });

      cancelledRef.current = false;
      cancelStreamingTextFlush();

      setState({
        phase: 'generating',
        actionId: editRequest.actionId,
        resultText: null,
        streamingText: null,
        intent: editRequest.intent,
        sourceText,
        promptUsed: prompt,
      });

      const controller = new AbortController();
      abortRef.current = controller;
      accumulatedRef.current = '';

      customFetchEventSource({
        path: `sessions/${sessionId}/ask-ai`,
        requestBody: {
          content: editor.getText(),
          ai_command_text: prompt,
          content_type: ContentType.MARKDOWN,
        },
        endPointVersion: 'v2-1',
        abortController: controller,
        onmessage: (ev) => {
          accumulatedRef.current +=
            ev.data === '' ? '\n' : ev.data.replace(/ {3}/g, '\n');
          scheduleStreamingTextFlush();
        },
      })
        .then(() => {
          if (
            cancelledRef.current ||
            !isEditorLive(editor, 'use-inline-selection-edit.streamThen')
          )
            return;

          const result = normalizeInlineEditResult(accumulatedRef.current);
          const range = getInlineEditPluginState(editor)?.range;
          if (!result || !range || range.from >= range.to) {
            toast({
              tone: 'destructive',
              title: intl.formatMessage(messages.editFailed),
            });
            reset();
            return;
          }
          if (!selectionSourceMatches(editor, range, sourceText)) {
            abortForStaleSelection();
            return;
          }

          dispatchInlineEditMeta(editor, {
            type: 'review-highlight',
            from: range.from,
            to: range.to,
          });
          cancelStreamingTextFlush();
          setState({
            phase: 'review',
            actionId: editRequest.actionId,
            resultText: result,
            streamingText: null,
            intent: editRequest.intent,
            sourceText,
            promptUsed: prompt,
          });
        })
        .catch((error: Error) => {
          if (cancelledRef.current || error.message === 'StreamManuallyAbortedError') {
            return;
          }
          if (isEditorLive(editor, 'use-inline-selection-edit.streamError')) {
            toast({
              tone: 'destructive',
              title: intl.formatMessage(messages.editFailed),
            });
          }
          reset();
        });
    },
    [
      editor,
      sessionId,
      intl,
      reset,
      abortForStaleSelection,
      setSelectedNoteText,
      cancelStreamingTextFlush,
      scheduleStreamingTextFlush,
    ]
  );

  useEffect(() => {
    if (!enabled || !request || request.editor !== editor) return;
    setRequest(null);
    if (isPromptInputRequest(request)) {
      const sourceText = getRangeSourceText(editor, {
        from: request.from,
        to: request.to,
      });
      if (!sourceText) {
        toast({
          tone: 'destructive',
          title: intl.formatMessage(messages.editFailed),
        });
        reset();
        return;
      }
      abortRef.current?.abort();
      setSelectedNoteText(null);
      dispatchInlineEditMeta(editor, {
        type: 'review-highlight',
        from: request.from,
        to: request.to,
      });
      setState({
        phase: 'prompting',
        actionId: request.actionId,
        resultText: null,
        streamingText: null,
        intent: request.intent,
        sourceText,
        promptUsed: null,
      });
      return;
    }
    start(request);
  }, [enabled, request, editor, setRequest, start, setSelectedNoteText, intl, reset]);

  useEffect(() => {
    if (!enabled && state.phase !== 'idle') {
      reset();
    }
  }, [enabled, state.phase, reset]);

  const submitPrompt = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (state.phase !== 'prompting' || !trimmed || !state.sourceText) return;
      const range = getInlineEditPluginState(editor)?.range;
      if (!range || range.from >= range.to) return;

      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          action: ButtonAction.AskHeidiSelectionAskAnythingClicked,
          label: 'ask_anything',
          click_source: 'inline_edit_bar',
          button_variant: 'inline_submit',
          session_id: sessionId || undefined,
          session_stage: sessionId ? sessionStage : undefined,
        },
        options: { amplitude: false },
      });

      start({
        editor,
        actionId: 'ask_anything',
        from: range.from,
        to: range.to,
        text: state.sourceText,
        intent: 'edit',
        customPrompt: composeSelectedTextQuestionPrompt(trimmed, state.sourceText),
      });
    },
    [editor, sessionId, sessionStage, state, start]
  );

  const trackBarAction = useCallback(
    (action: ButtonAction, buttonVariant?: string) => {
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          action,
          label: state.actionId ?? 'unknown',
          click_source: 'inline_edit_bar',
          session_id: sessionId || undefined,
          session_stage: sessionId ? sessionStage : undefined,
          ...(buttonVariant && { button_variant: buttonVariant }),
        },
        options: { amplitude: false },
      });
    },
    [sessionId, sessionStage, state.actionId]
  );

  const trackFeedback = useCallback(
    (feedback: 'positive' | 'negative') => {
      track({
        event_name: TrackingEventName.FeedbackSubmitted,
        props: {
          type: 'thumbs',
          value: feedback === 'positive' ? 'up' : 'down',
          target_type: 'inline_selection_ai',
          target_id: state.actionId ?? 'unknown',
          session_id: sessionId || undefined,
        },
        options: { amplitude: false },
      });
    },
    [sessionId, state.actionId]
  );

  const openEvidencePanel = useOpenEvidencePanel();
  const stageAskHeidiQuery = useStageAskHeidiQuery(sessionId, openEvidencePanel);

  const viewInTranscript = useCallback(() => {
    if (
      state.phase !== 'review' ||
      state.intent !== 'insight' ||
      state.actionId !== 'view_source' ||
      !sessionId ||
      !state.resultText
    ) {
      return;
    }

    const excerpts = parseQuoteShapedTranscriptExcerpts(state.resultText);
    if (excerpts.length === 0) {
      toast({
        tone: 'destructive',
        title: intl.formatMessage(messages.noTranscriptExcerpts),
      });
      return;
    }

    const evidence = excerpts.map((excerpt) => excerpt.text);

    setTranscriptEvidence({
      evidence,
      evidenceTimestamps: excerpts.map((excerpt) => excerpt.timestamp),
      isShowing: true,
      sessionId,
      entityId: '',
      allowWordRunFallback: excerpts.some((excerpt) => excerpt.timestamp != null),
      returnOpensEntityVerificationDrawer: false,
    });
    ensureTabFocus(TabType.TRANSCRIPT);
    scrollToTranscriptHighlight();

    trackBarAction(ButtonAction.AskHeidiInlineInsightViewInTranscript);
    reset();
  }, [
    state,
    sessionId,
    intl,
    setTranscriptEvidence,
    ensureTabFocus,
    trackBarAction,
    reset,
  ]);

  const askInChat = useCallback(() => {
    if (state.phase !== 'review' || !state.promptUsed) return;
    const prompt =
      state.intent === 'insight' &&
      state.actionId &&
      state.sourceText &&
      isSelectionInsightActionId(state.actionId)
        ? composeSelectionActionPrompt(state.actionId, state.sourceText)
        : state.promptUsed;
    stageAskHeidiQuery({
      message: prompt,
      entryMethod: 'selection_action',
      chipMeta: {
        label: state.actionId ?? 'ask_anything',
        category: 'selection',
        surface: 'selection_menu',
      },
    });
    trackBarAction(ButtonAction.AskHeidiInlineEditAskInChat);
    reset();
  }, [state, stageAskHeidiQuery, trackBarAction, reset]);

  const accept = useCallback(() => {
    if (state.phase !== 'review' || !state.resultText || state.intent !== 'edit') return;
    if (!isEditorLive(editor, 'use-inline-selection-edit.accept')) {
      reset();
      return;
    }
    const range = getInlineEditPluginState(editor)?.range;
    if (!range || range.to <= range.from) {
      abortForStaleSelection();
      return;
    }
    if (!state.sourceText || !selectionSourceMatches(editor, range, state.sourceText)) {
      abortForStaleSelection();
      return;
    }
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContentAt(
        range.from,
        toInsertableHtml(state.resultText, { lists: editorSupportsLists(editor) })
      )
      .run();
    trackBarAction(ButtonAction.AskHeidiInlineEditAccepted, 'replace');
    reset();
  }, [editor, state, reset, trackBarAction, abortForStaleSelection]);

  const insertBelow = useCallback(() => {
    if (state.phase !== 'review' || !state.resultText || state.intent !== 'edit') return;
    if (!isEditorLive(editor, 'use-inline-selection-edit.reject')) {
      reset();
      return;
    }
    const range = getInlineEditPluginState(editor)?.range;
    if (!range || range.to <= range.from) {
      abortForStaleSelection();
      return;
    }
    if (!state.sourceText || !selectionSourceMatches(editor, range, state.sourceText)) {
      abortForStaleSelection();
      return;
    }
    const $to = editor.state.doc.resolve(range.to);
    const insertPos = $to.after($to.depth);
    editor
      .chain()
      .focus()
      .insertContentAt(
        insertPos,
        toInsertableBlockHtml(state.resultText, { lists: editorSupportsLists(editor) })
      )
      .run();
    trackBarAction(ButtonAction.AskHeidiInlineEditAccepted, 'insert_below');
    reset();
  }, [editor, state, reset, trackBarAction, abortForStaleSelection]);

  const cancel = useCallback(
    (buttonVariant: 'stop' | 'discard') => {
      trackBarAction(ButtonAction.AskHeidiInlineEditDismissed, buttonVariant);
      const restoreRange =
        state.phase === 'prompting' ? getInlineEditPluginState(editor)?.range : null;
      reset();
      if (
        restoreRange &&
        isEditorLive(editor, 'use-inline-selection-edit.restoreSelection') &&
        restoreRange.from < restoreRange.to
      ) {
        editor.chain().focus().setTextSelection(restoreRange).run();
      }
    },
    [reset, trackBarAction, state.phase, editor]
  );

  const stop = useCallback(() => cancel('stop'), [cancel]);
  const discard = useCallback(() => cancel('discard'), [cancel]);

  const reprompt = useCallback(() => {
    if (state.phase !== 'review' || state.intent !== 'edit') return;
    const range = getInlineEditPluginState(editor)?.range;
    if (
      !range ||
      range.from >= range.to ||
      !isEditorLive(editor, 'use-inline-selection-edit.confirm')
    ) {
      discard();
      return;
    }
    trackBarAction(ButtonAction.AskHeidiInlineEditDismissed, 'undo');
    dispatchInlineEditMeta(editor, {
      type: 'review-highlight',
      from: range.from,
      to: range.to,
    });
    setState((prev) => ({
      ...prev,
      phase: 'prompting',
      resultText: null,
      streamingText: null,
      promptUsed: null,
    }));
  }, [editor, state, discard, trackBarAction]);

  useEffect(() => {
    return () => {
      cancelStreamingTextFlush();
    };
  }, [cancelStreamingTextFlush]);

  useEffect(() => {
    if (state.phase === 'idle') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancel(state.phase === 'generating' ? 'stop' : 'discard');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [state.phase, cancel]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      abortRef.current?.abort();
    };
  }, [editor]);

  return {
    phase: state.phase,
    actionId: state.actionId,
    resultText: state.resultText,
    streamingText: state.streamingText,
    intent: state.intent,
    isUnanswered:
      state.phase === 'review' && isUnansweredInlineResponse(state.resultText ?? ''),
    accept,
    insertBelow,
    discard,
    reprompt,
    stop,
    viewInTranscript,
    submitPrompt,
    askInChat,
    trackFeedback,
  };
};
